/**
 * Recovery and reporting for stale lazily-loaded chunks.
 *
 * Vite fingerprints every chunk, so a tab left open across a deploy holds HTML
 * pointing at filenames the running release no longer serves. The main bundle is
 * already in memory and keeps working; the failure only shows up the first time
 * something is imported lazily, which is why it read as "the charts disappear"
 * rather than "the page broke".
 *
 * Bridging public/ in config/deploy.yml is the real fix and should stop this
 * happening at all. This is the belt: a CDN edge, a cache, or a future asset
 * change can reintroduce the same shape, and a blank chart with no signal is
 * exactly what cost us a month of not knowing.
 */
const RELOAD_FLAG = "ic:chunk-reload"

export function reportClientError(kind: string, message: string, extra: Record<string, unknown> = {}): void {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!token) return

  // keepalive so the report still goes out when we reload immediately after.
  void fetch("/client_errors", {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify({ kind, message, page: window.location.pathname, ...extra })
  }).catch(() => {
    // Reporting must never become its own failure. Swallow.
  })
}

/**
 * Reload once when a chunk fails to load, then never again for that page load.
 *
 * The guard matters: if a chunk is genuinely missing rather than merely stale,
 * an unguarded reload is an infinite refresh loop pointed at production. One
 * attempt fixes the stale case (fresh HTML, current filenames) and gives up
 * cleanly on the permanent one, leaving the error boundary to show its message.
 */
export function installChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const message = event.payload?.message ?? "vite:preloadError"

    reportClientError("chunk_load_failed", message, {
      already_retried: sessionStorage.getItem(RELOAD_FLAG) === "1"
    })

    if (sessionStorage.getItem(RELOAD_FLAG) === "1") return

    event.preventDefault()
    sessionStorage.setItem(RELOAD_FLAG, "1")
    window.location.reload()
  })

  // A successful load means whatever went wrong is behind us; clear the guard so
  // a deploy next week can recover the same way.
  window.addEventListener("load", () => sessionStorage.removeItem(RELOAD_FLAG))
}
